// InterviewStore: domain state and orchestration across API, recorder, realtime, uploader
import { makeAutoObservable, runInAction, reaction } from "mobx";
import { startInterval } from "../services/clock";
import { TOOLS } from "../components/OpenAiTools";
import RotationStore from "./rotation.store";
// import { RECORDER_STATES } from "./recorder.store";

export const INTERVIEW_STATES = {
  IDLE: "Idle",
  STARTING: "Starting",
  IN_PROGRESS: "In Progress",
  FINALIZING: "Finalizing",
  FINALIZED: "Finalized",
  ERROR: "Error",
};

export class InterviewStore {
  /**
   * @param {{ api: any, recorderStore: any, uploaderStore: any, realtimeStore: any, logShipper?: any }} deps
   */
  constructor({ api, recorderStore, uploaderStore, realtimeStore, logShipper }) {
    this.api = api;
    this.recorder = recorderStore;
    this.uploader = uploaderStore;
    this.realtime = realtimeStore;
    this.logShipper = logShipper;
    this.rotation = new RotationStore(this, realtimeStore);

    this.state = INTERVIEW_STATES.IDLE;
    this.applicationId = null;
    this.recordingId = null;
    // Per-section transcript map: { [sectionId]: string }
    this.sectionTranscripts = {}; // replaces array approach
    this.part = 0; // legacy part index retained for compatibility (can be removed later)
    this.durationSec = 0; // initial total duration from API (used only to seed startTimeMs)
    this.currentSection = null;
    this.currentSectionDurationSec = 0;
    this.startTimeMs = null;
    this.sectionStartTimeMs = null;
    // Reactive timers driven by a single interval instead of Date.now() polling in the view
    this.elapsedSec = 0; // total interview elapsed seconds (seeded from API)
    this.sectionElapsedSec = 0; // current section elapsed seconds (seeded from API)
    this.isFinished = false;
    this.result = null;

    this._starting = false;
    this._finalizing = false;
    this._persistTick = null;
    this._timingTick = null;
    this._logFlushTick = null;
    this._visibilityHandler = null;
    this._sectionAdvancing = false; // true only while rotateSection network hop is in flight
    this._awaitingPartFinish = false; // true after user requests nextSection until tool call arrives
    this._userSpeaking = false;
    this.wrapSoon = false; // UI-only indicator, no model prompt
    this._partFinishRequested = false; // prevents duplicate part finish requests at boundary
    this._speechStopUnsub = null;

    // Mark ephemeral flags/non-state as non-observable to avoid strict-mode noise
    makeAutoObservable(this, {
      _starting: false,
      _finalizing: false,
      _persistTick: false,
      _timingTick: false,
      _logFlushTick: false,
      _visibilityHandler: false,
      _sectionAdvancing: false,
      _awaitingPartFinish: false,
      _userSpeaking: false,
      wrapSoon: true,
      _partFinishRequested: false,
    }, { autoBind: true });

    // React only to transport-level reconnect events (isReconnect true) while an
    // interview is in progress. This avoids double-sending on initial connect
    // or page refresh, and only sends a reconnect-style message when the
    // realtime service has actually reconnected.
    reaction(
      () => this.realtime.reconnectCount,
      (count) => {
        if (!count) return; // ignore initial zero
        try {
          console.log("[InterviewStore] reconnect reaction", {
            reconnectCount: count,
            state: this.state,
            hasCurrentSection: !!this.currentSection?._id,
          });
        } catch (e) { void e; }
        if (this.state !== INTERVIEW_STATES.IN_PROGRESS) return;
        this._sendSectionStartOrReconnect();
      }
    );
  }

  _sendSectionStartOrReconnect() {
    if (!this.currentSection?._id) return;

    // Flush any buffered transcript from realtime into sectionTranscripts
    const buffer = this.realtime.consumeTranscriptBuffer();
    if (buffer) this.appendTranscriptForCurrentSection(buffer);

    const existingTranscript = (this.currentTranscript || "").trim();

    // If this is a reconnect and we have transcript, send reconnect message
    if (this.realtime.isReconnect && existingTranscript) {
      this.realtime.sendReconnectMessage({
        transcriptsForPart: existingTranscript,
        repeatLastQuestion: false,
      });
      return;
    }

    // Otherwise, only send the initial message if there is no transcript yet
    if (!existingTranscript) {
      // Auto-mute user mic during section intro
      this.recorder.startAutoMute();
      this.realtime.sendInitialMessage();
    } else {
      // Resume with transcript even if isReconnect is false (e.g. page refresh)
      this.realtime.sendReconnectMessage({
        transcriptsForPart: existingTranscript,
        repeatLastQuestion: false,
      });
    }
  }

  get currentTranscript() {
    if (!this.currentSection?._id) return "";
    return this.sectionTranscripts[this.currentSection._id] || "";
  }

  get fullTranscript() {
    return Object.values(this.sectionTranscripts).join("\n\n");
  }

  /**
   * Send a reconnect prompt to the model using the current section transcript.
   * Mirrors legacy OpenAiConnection behavior by:
   *  - Passing the full transcript for the current section
   *  - Asking (by default) to evaluate the last answer and move on or follow up
   */
  sendReconnect({ repeatLastQuestion = false } = {}) {
    const transcript = (this.currentTranscript || "").trim();
    // Log for debugging: how much context we're sending
    try {
      console.log("[InterviewStore] sendReconnect", {
        sectionId: this.currentSection?._id,
        order: this.currentSection?.order,
        transcriptChars: transcript.length,
      });
    } catch (e) { void e; }
    this.realtime.sendReconnectMessage({
      transcriptsForPart: transcript,
      repeatLastQuestion,
    });
  }

  async start(applicationId, { audioEl }) {
    if (this._starting) return;
    this._starting = true;
    this.applicationId = applicationId;
    this.state = INTERVIEW_STATES.STARTING;
    try {
      const sectionResp = await this.api.startAiInterviewPart({ applicationId });
      const interview = await this.api.startInterview({ applicationId });
      runInAction(() => {
        this.recordingId = interview.recordingId;
        this.durationSec = interview.durationSec || 0;
        this.currentSectionDurationSec = interview.currentSectionDurationSec || 0;
        this.currentSection = {
          _id: sectionResp.sectionId,
          name: sectionResp.name,
          durationSec: sectionResp.sectionDurationSec,
          isLast: Boolean(sectionResp.isLast),
          order: typeof sectionResp.order === "number" ? sectionResp.order : 0,
        };
        if (Array.isArray(interview.transcripts) && interview.transcripts.length && typeof this.currentSection.order === "number") {
          // Map backend transcripts array by section order to the per-section transcript map.
          // Note: order is assumed to be a zero-based index into interview.transcripts.
          const idx = this.currentSection.order >= 0 && this.currentSection.order < interview.transcripts.length
            ? this.currentSection.order
            : null;
          if (idx !== null) {
            const baseTranscript = interview.transcripts[idx];
            if (typeof baseTranscript === "string" && baseTranscript.trim()) {
              this.sectionTranscripts[this.currentSection._id] = baseTranscript;
            }
          }
        }
        if (this.currentSection?._id && !this.sectionTranscripts[this.currentSection._id]) {
          this.sectionTranscripts[this.currentSection._id] = "";
        }
        // Seed precise timers using API durations
        this.startTimeMs = Date.now() - (this.durationSec * 1000);
        this.sectionStartTimeMs = Date.now() - (this.currentSectionDurationSec * 1000);
      });

      await this.recorder.init();
      // Expect recorder stream to be available via StreamingMic integration
      const micStream = new MediaStream(this.recorder.stream.getAudioTracks());
      await this.realtime.connect({
        key: sectionResp.key,
        inputStream: micStream,
        audioEl,
        part: this.part,
        startTimeMs: this.startTimeMs,
        section: this.currentSection,
        sectionStartTimeMs: this.sectionStartTimeMs,
        tools: TOOLS,
        applicationId: this.applicationId,
      });

      // Inject callbacks for section advance and interview finish
      this.realtime._onSectionAdvance = () => {
        // Rotation store handles all timing boundaries
        // When part_finished arrives, use rotation store to ensure audio completes
        if (this.currentSection?.isLast) {
          this.askToFinish({ force: false, reason: "last-section" }).catch(() => { });
        } else {
          // Use rotation store's _attemptRotation to defer if audio is playing
          this.rotation._attemptRotation('part_finished_callback');
        }
      };

      this.realtime._onInterviewFinished = (result) => {
        this.finishWithResult(result).catch(() => { });
      };

      // On realtime connected, start recorder
      await this.recorder.start();
      runInAction(() => {
        this.state = INTERVIEW_STATES.IN_PROGRESS;
        // Seed reactive counters from API supplied durations
        this.elapsedSec = this.durationSec;
        this.sectionElapsedSec = this.currentSectionDurationSec;
      });
      // Initialize rotation store for first section
      this.rotation.startSection();
      this._startTimingLoop();

      // Auto-manage section endings on speech stop when duration reached
      try {
        this._speechStopUnsub?.();
      } catch { /* noop */ }
      const onSpeechStopped = () => {
        // Update speaking flag
        runInAction(() => { this._userSpeaking = false; });
        // Notify rotation store
        this.rotation.onUserSpeechStopped();
      };
      const onSpeechStarted = () => {
        runInAction(() => { this._userSpeaking = true; });
      };
      const onOutputStopped = () => {
        // Notify rotation store when assistant stops speaking
        this.rotation.onAssistantSpeechStopped();
        // End auto-mute when assistant finishes speaking (section intro done)
        this.recorder.endAutoMute();
      };
      this.realtime.service.on('speechStopped', onSpeechStopped);
      this.realtime.service.on('speechStarted', onSpeechStarted);
      this.realtime.service.on('outputStopped', onOutputStopped);
      this._speechStopUnsub = () => {
        this.realtime.service.off('speechStopped', onSpeechStopped);
        this.realtime.service.off('speechStarted', onSpeechStarted);
        this.realtime.service.off('outputStopped', onOutputStopped);
      };

      // Kick off or resume the conversation using unified policy
      this._sendSectionStartOrReconnect();

      // Start debounced persistence loop
      this._persistTick?.(); // dispose if exists
      this._persistTick = startInterval(() => {
        if (this.state === INTERVIEW_STATES.IN_PROGRESS) {
          this.updateProgress().catch(() => { });
        }
      }, 5000);

      // Periodically ship buffered debug logs (always-on, independent of #dev LogViewer)
      this._logFlushTick?.();
      this._logFlushTick = startInterval(() => {
        this.logShipper?.flush({ recordingId: this.recordingId, sectionId: this.currentSection?._id }).catch(() => { });
      }, 20000);

      // Tail-loss guard: flush on tab-hide so the last ~20s of logs aren't lost on close.
      try { if (this._visibilityHandler) document.removeEventListener("visibilitychange", this._visibilityHandler); } catch (e) { void e; }
      this._visibilityHandler = () => {
        if (document.visibilityState === "hidden") {
          this.logShipper?.flush({ recordingId: this.recordingId, sectionId: this.currentSection?._id }).catch(() => { });
        }
      };
      document.addEventListener("visibilitychange", this._visibilityHandler);
    } catch (e) {
      runInAction(() => { this.state = INTERVIEW_STATES.ERROR; });
      throw e;
    } finally {
      runInAction(() => { this._starting = false; });
    }
  }

  appendTranscriptForCurrentSection(text) {
    if (!this.currentSection?._id || !text) return;
    const id = this.currentSection._id;
    const current = this.sectionTranscripts[id] || "";
    this.sectionTranscripts[id] = current + text;
  }

  async rotateSection() {
    if (!this.applicationId || !this.currentSection?._id) return;
    // If this is the last section, finish instead of rotating
    if (this.currentSection?.isLast) {
      await this.askToFinish({ force: false, reason: "last-section" });
      return;
    }
    // Request next section key using current section id as hint
    if (this._sectionAdvancing) return;
    this._sectionAdvancing = true;
    const oldSectionId = this.currentSection?._id;
    try {
      const next = await this.api.startAiInterviewPart({ applicationId: this.applicationId, sectionId: this.currentSection._id });
      // Best-effort flush of the old section's buffered logs before context switches
      await this.logShipper?.flush({ recordingId: this.recordingId, sectionId: oldSectionId }).catch(() => { });
      // Capture the candidate's final answer (async Whisper transcription) before flushing/teardown.
      // currentSection is still the outgoing section here, so the answer is attributed correctly.
      await this.realtime.waitForInputTranscriptionsSettled({ timeoutMs: 2000 });
      // Flush any buffered transcript before switching
      const buffer = this.realtime.consumeTranscriptBuffer();
      if (buffer) this.appendTranscriptForCurrentSection(buffer);
      const newSection = {
        _id: next.sectionId,
        name: next.name,
        durationSec: next.sectionDurationSec,
        isLast: Boolean(next.isLast),
      };
      runInAction(() => {
        if (!this.sectionTranscripts[newSection._id]) this.sectionTranscripts[newSection._id] = "";
        this.currentSection = newSection;
        this.currentSectionDurationSec = 0;
        this.sectionStartTimeMs = Date.now();
        this.sectionElapsedSec = 0; // reset reactive counter
        this._userSpeaking = false;
        this.wrapSoon = false;
        this._partFinishRequested = false;
      });
      // Initialize rotation store for new section
      this.rotation.startSection();
      await this.realtime.reconnect({ key: next.key, section: newSection, sectionStartTimeMs: this.sectionStartTimeMs, applicationId: this.applicationId });
      // Start or resume the next section explicitly using unified policy
      this._sendSectionStartOrReconnect();
    } catch (e) {
      console.error("[InterviewStore] rotateSection error", e);
    } finally {
      runInAction(() => {
        this._sectionAdvancing = false;
        this._awaitingPartFinish = false;
      });
    }
  }

  async updateProgress() {
    if (!this.recordingId) return;
    // Skip updates during section rotation to avoid sending stale data
    if (this._sectionAdvancing) return;
    // Incorporate buffered transcript from realtime if present
    const buffer = this.realtime.consumeTranscriptBuffer();
    if (buffer) {
      this.appendTranscriptForCurrentSection(buffer);
    }
    const transcript = this.currentTranscript;
    await this.api.updateInterview({
      recordingId: this.recordingId,
      transcript,
      durationSec: this.elapsedSec,
      currentSectionId: this.currentSection?._id,
      currentSectionDurationSec: this.sectionElapsedSec,
    });
  }

  async askToFinish({ force = false, reason = "time", isByUser = false } = {}) {
    if (this._finalizing) return;
    if (isByUser) {
      this.appendTranscriptForCurrentSection("\n\nCandidate requested to finish the interview.\n\n");
    }
    // Mark that we're in a finishing request to avoid duplicates, but do NOT switch
    // to Finalizing state yet. We only show the saving modal once the assistant has
    // finished speaking and we're actually stopping/saving the recording.
    runInAction(() => {
      this._finalizing = true;
    });
    try {
      this.realtime.sendFinishRequest({ force, reason });
      // Result will be delivered via function call in realtime; provide a watchdog fallback if needed
    } finally {
      runInAction(() => { this._finalizing = false; });
    }
  }

  /**
   * User-triggered request to advance to the next section. Non-force will ask the model
   * to gracefully conclude and emit part_finished (which triggers rotateSection via function call).
   * If forceImmediate is true, we bypass the model and rotate directly.
   */
  nextSection({ forceImmediate = false } = {}) {
    if (this.state !== INTERVIEW_STATES.IN_PROGRESS) return;
    if (this._sectionAdvancing) return; // guard against spam
    if (forceImmediate) {
      if (this.currentSection?.isLast) {
        this.askToFinish({ force: false, reason: "last-section" });
      } else {
        this.rotateSection();
      }
      return;
    }
    // Ask model to finish current part; rotation occurs after function_call tool execution.
    try {
      if (this.currentSection?.isLast) {
        // Last section: finish interview gracefully
        this.askToFinish({ force: false, reason: "last-section" });
      } else {
        this.realtime.service?.sendPartFinishRequest?.({ force: false });
      }
    } catch (e) { void e; }
    // Mark awaiting tool callback; rotation will clear this.
    this._awaitingPartFinish = true;
  }

  async finishWithResult({ feedback = "", score = 0 } = {}) {
    if (this.state === INTERVIEW_STATES.FINALIZED) return;
    // Ensure all assistant speech is recorded before we stop/switch to saving state.
    // If assistant is still speaking (e.g., saying goodbye), wait for outputStopped (with a short timeout fallback).
    try {
      if (this.realtime?.assistantSpeaking) {
        await new Promise((resolve) => {
          let done = false;
          const finish = () => { if (done) return; done = true; resolve(); };
          const handler = () => { try { this.realtime?.service?.off?.('outputStopped', handler); } catch (e) { void e; } finish(); };
          try { this.realtime?.service?.on?.('outputStopped', handler); } catch (e) { void e; }
          // Fallback in case we missed the event or it never arrives
          setTimeout(() => {
            try { this.realtime?.service?.off?.('outputStopped', handler); } catch (e) { void e; }
            finish();
          }, 8000);
        });
      }
    } catch (e) { void e; }
    runInAction(() => { this.state = INTERVIEW_STATES.FINALIZING; });
    try {
      // Capture the candidate's final answer of the last section before the final flush/teardown.
      await this.realtime.waitForInputTranscriptionsSettled({ timeoutMs: 2000 });
      // Final flush of buffered transcript before stopping
      const buffer = this.realtime.consumeTranscriptBuffer();
      if (buffer) this.appendTranscriptForCurrentSection(buffer);
      await this.recorder.stop(true);
      await this.uploader.waitForDrain();
      // Feedback/score are persisted server-side from the observed interview_finished
      // (webrtcfilter relay). Omit them here so the backend keeps the observed values
      // instead of overwriting with client defaults.
      await this.api.stopInterview({
        recordingId: this.recordingId,
        transcript: this.currentTranscript || "",
        currentSectionId: this.currentSection?._id,
      });
      // Best-effort final flush of buffered debug logs before tearing down
      await this.logShipper?.flush({ recordingId: this.recordingId, sectionId: this.currentSection?._id }).catch(() => { });
      // Close realtime connection after finalization to release resources
      try { this.realtime.teardown(); } catch (e) { void e; }
      runInAction(() => {
        this.isFinished = true;
        this.state = INTERVIEW_STATES.FINALIZED;
        this.result = { feedback, score };
      });
    } catch (e) {
      runInAction(() => { this.state = INTERVIEW_STATES.ERROR; });
      throw e;
    }
  }

  // Legacy helpers (kept for any existing callers) now delegate to reactive counters
  _computeElapsedSec() { return this.elapsedSec; }
  _computeSectionElapsedSec() { return this.sectionElapsedSec; }

  _startTimingLoop() {
    // Dispose existing loop if any
    try { this._timingTick?.(); } catch (e) { void e; }
    this._timingTick = startInterval(() => {
      if (this.state !== INTERVIEW_STATES.IN_PROGRESS) return;
      runInAction(() => {
        this.elapsedSec += 1;
        this.sectionElapsedSec += 1;
      });
      // Soft warning (UI only) 15s before end; do not send any model message
      const max = this.currentSection?.durationSec || 0;
      if (max > 0) {
        const remaining = max - this.sectionElapsedSec;
        runInAction(() => { this.wrapSoon = remaining <= 15 && remaining > 0; });

        // Delegate all boundary logic to rotation store
        this.rotation.tick(this.sectionElapsedSec, max);
      }
    }, 1000);
  }

  dispose() {
    try { this.rotation.reset(); } catch (e) { void e; }
    try { this.realtime.teardown(); } catch (e) { void e; }
    try { this.recorder.dispose(); } catch (e) { void e; }
    try { this._persistTick?.(); } catch (e) { void e; }
    try { this._timingTick?.(); } catch (e) { void e; }
    try { this._logFlushTick?.(); } catch (e) { void e; }
    try { if (this._visibilityHandler) document.removeEventListener("visibilitychange", this._visibilityHandler); } catch (e) { void e; }
    try { this._speechStopUnsub?.(); } catch (e) { void e; }
  }
}
